import { Amplify } from "aws-amplify"
import { del, get, post, put } from "aws-amplify/api"
import { config, isProd } from "../config"
import { currentSession } from "../Utils.AmplifyAuth"

export interface ApiSuccessResponse<T> {
  success: true
  data: T
  message?: string
}

export interface ApiErrorResponse {
  success: false
  error: string
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

/**
 * Performs a GET operation on an api
 *
 * Sets default headers.
 * Does some error unmarshalling.
 * @param apiName
 * @param path
 * @returns
 */
export const getFromApi = async (apiName: string, path: string) => {
  const options = await getDefaultOptions()
  // console.log(`GET ${apiName} / ${path}`)
  const restOperation = get({ apiName, path, options })
  return await unwrapAndErrorHandling(restOperation)
}

/**
 * Performs a PUT operation on an api.
 *
 * Sets default headers.
 * Does some error unmarshalling.
 * @param apiName
 * @param path
 * @param body
 * @returns
 */
export const putToApi = async (apiName: string, path: string, body: any) => {
  const options = await getDefaultOptions(body)
  // console.log(`PUT ${apiName} / ${path}`)
  const restOperation = put({ apiName, path, options })
  return await unwrapAndErrorHandling(restOperation)
}

/**
 * Performs a POST operation on an api.
 *
 * Sets default headers.
 * Does some error unmarshalling.
 * @param apiName
 * @param path
 * @param body
 * @returns
 */
export const postToApi = async (apiName: string, path: string, body: any) => {
  const options = await getDefaultOptions(body)
  // console.log(`POST ${apiName} / ${path}`)
  const restOperation = post({ apiName, path, options })
  return await unwrapAndErrorHandling(restOperation)
}

/**
 * Performs a DEL operation on an api.
 *
 * Sets default headers.
 * Does some error unmarshalling.
 * @param apiName
 * @param path
 * @returns
 */
export const delToApi = async (apiName: string, path: string) => {
  const options = await getDefaultOptions()
  // console.log(`DEL ${apiName} / ${path}`)
  const restOperation = del({ apiName, path, options })
  return await unwrapAndErrorHandling(restOperation)
}

const getDefaultOptions = async (body: any = null) => {
  const tokens = await currentSession()
  if (!tokens) {
    throw new Error("Got no tokens, cannot load user data")
  }
  const options = {
    headers: {
      Authorization: `Bearer ${tokens?.idToken}`,
    },
    retryStrategy: {
      strategy: "no-retry" as const, // Overrides default retry strategy
    },
    body: body,
  }
  if (!body) {
    delete options.body
  }
  return options
}

const unwrapAndErrorHandling = async (restOperation: any) => {
  try {
    const response = await restOperation.response
    const json = await response.body.json()
    // console.log("unwrapAndErrorHandling.response = ", response)
    return { json, response }
  } catch (err) {
    // This error handler unpacks and repacks the API response, because sometimes the format is weird (as for the generate-statistics-backend).
    // err may look like
    //  - { "response": { "body": "", "statusCode": 400, } }
    //  - { "_response": { "body": "", "statusCode": 400, } }
    // This handler should throw an error that looks like
    //  - { "body": "", "statusCode": 400, }
    // console.log("🪅 unwrapAndErrorHandling.error = ", JSON.stringify(err, null, 2))
    const errAsAny = err as any
    // Special handling for network errors (e.g. CORS violations)
    if (errAsAny.name === "NetworkError") {
      console.log("🪅 unwrapAndErrorHandling is network error")
      throw {
        body: isProd
          ? "Netwerkfehler."
          : errAsAny.name +
            " (might be caused by CORS, check you browser's log)",
      }
    }
    const errResponse = errAsAny.response ?? errAsAny._response
    // console.log("unwrapAndErrorHandling.errResponse = ", errResponse)
    const normalizedError = errResponse
      ? {
          ...errResponse,
          body: JSON.parse(errResponse.body),
        }
      : {
          body: "Unknown error format: " + JSON.stringify(err, null, 2),
        }
    throw normalizedError
  }
}

export const getCacheKey = (apiName: string, path: string) =>
  `${apiName}#${path}`

export const configureApis = () => {
  // Configure APIs so they are known
  const existingConfig = Amplify.getConfig()
  Amplify.configure({
    ...existingConfig,
    API: {
      ...existingConfig.API,
      REST: {
        ...existingConfig.API?.REST,
        frontendApi: {
          endpoint: config.frontendApi.URL,
          region: config.frontendApi.REGION,
        },
        vehiclesApi: {
          // the legacy SDS vehicles api, e.g. vehicles.list
          endpoint: config.apiGateway.URL,
          region: config.apiGateway.REGION,
        },
      },
    },
  })
}
